import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TrustSafety } from "@/components/TrustSafety";
import { PremiumComparisonTable } from "@/components/PremiumComparisonTable";
import { HowItWorksSimple } from "@/components/HowItWorksSimple";
import { FAQ } from "@/components/FAQ";
import { SkipToContent } from "@/components/SkipToContent";
import { useNavigate } from 'react-router';
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/hooks/useAuth";
import { useDynamicCategories } from "@/hooks/useDynamicCategories";
import { useCountryConfig } from "@/lib/countryConfig";
import { searchServices } from "@/lib/serviceSearch";
import { trackAnalyticsEvent } from "@/lib/analytics/openpanel";
import { usePageMeta } from "@/hooks/usePageMeta";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { Search, Shield, Star, ShieldCheck, ChevronRight, Phone } from "lucide-react";
import { categoryImage } from "@/lib/categoryImages";
// How many service cards the home page previews before "See all". Eight is
// four rows at the phone's two-per-row and two at the desktop's four.
const HOME_SERVICE_PREVIEW = 8;


export default function Index() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedServiceCategory, setSelectedServiceCategory] = useState("");
  const [serviceOpen, setServiceOpen] = useState(false);
  // The hero <section> sets overflow-hidden (it clips the marquee), which also
  // clipped this dropdown mid-row — no z-index escapes an ancestor's overflow.
  // So the list renders in a portal on <body>, positioned against the pill's
  // live rect and capped to the space actually left on screen.
  const searchPillRef = useRef<HTMLDivElement>(null);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  const { config } = useCountryConfig();

  // Pause the hero marquee animation while it is scrolled out of view
  // (class toggle so the CSS :hover pause keeps working when visible).
  const marqueeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = marqueeRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      el.classList.toggle("marquee-offscreen", !entry.isIntersecting);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  usePageMeta(
    `Kanfy ${config.name} | Find Verified House Helpers, Cleaners & Nannies`,
    `Book verified, background-checked home helpers in ${config.name}. Starting from ${config.currencyCode} 15,000/hr.`
  );

  const { heroCategories, popularCategories, categories } = useDynamicCategories();

      // Dynamic dropdown options: services come from the admin Categories panel
      // (via useDynamicCategories), locations come from the admin Operational
      // Regions panel (via useCountryConfig) — neither is hardcoded anymore.
      // Typeahead options: each admin category + its active subcategories, each
      // pointing back to its parent category (so a picked subcategory books
      // under the right category). No hardcoded service list.
      // Default (nothing typed): the major categories only. Once the visitor
      // types, a matching category expands into everything it offers, and
      // matching subcategories surface tagged with their parent. Sourced
      // exclusively from the admin-active catalogue — see lib/serviceSearch.
      const filteredServices = searchServices(categories, searchQuery);

      // Keep the portalled menu glued to the pill while it's open.
      useLayoutEffect(() => {
        if (!serviceOpen) { setMenuRect(null); return; }
        const measure = () => {
          const el = searchPillRef.current;
          if (!el) return;
          const r = el.getBoundingClientRect();
          const GAP = 8;
          const BOTTOM_MARGIN = 16;
          setMenuRect({
            top: r.bottom + GAP,
            left: r.left,
            width: r.width,
            // Never taller than the room left below the pill, so the last row
            // is always reachable by scrolling inside the menu.
            maxHeight: Math.max(160, window.innerHeight - r.bottom - GAP - BOTTOM_MARGIN),
          });
        };
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
          window.removeEventListener('resize', measure);
          window.removeEventListener('scroll', measure, true);
        };
      }, [serviceOpen]);

      const handleServicePick = (opt: { label: string; category: string }) => {
        setSearchQuery(opt.label);
        setSelectedServiceCategory(opt.category);
        setServiceOpen(false);
      };

  // Map DB categories to the shapes the JSX expects
  const heroServices = heroCategories.map((cat) => ({
    title: cat.name,
    subtitle: cat.description ?? "",
    badge: cat.requires_certifications ? "Pro Verified" : "Verified",
    // badgeColor/bg were dead data — the carousel hardcodes its badge colour
    // and never read `bg` (a slate gradient from the pre-Kanfy palette).
    image: categoryImage(cat.slug, cat.image_url),
  }));

  const serviceCategories = categories.map((cat) => ({
    label: cat.name,
    query: cat.name,
    color: "bg-blue-50 text-blue-700 border-blue-200",
  }));

  // Real per-category stats (v_category_stats via useDynamicCategories). Each
  // field is omitted when there is no data — no more identical fabricated
  // price/rating/count line repeated on every card.
  const services = popularCategories.map((cat) => ({
    title: cat.name,
    price: cat.min_rate && cat.min_rate > 0
      ? `From ${config.currencyCode} ${cat.min_rate.toLocaleString(config.locale)}`
      : null,
    rating: cat.avg_rating ?? null,
    // Prefer helper availability (what a booker cares about); only shown when > 0.
    helpers: cat.helper_count && cat.helper_count > 0 ? cat.helper_count : null,
    image: categoryImage(cat.slug, cat.image_url),
  }));

  // Task-first entry (booking-journey remake): the hero "Find Helpers" and the
  // category tiles launch the full booking flow, seeded with the service +
  // location, rather than dropping the client on a helper-browse page. Helpers
  // are matched inside the flow after the task is scoped.
  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) {
      if (!selectedServiceCategory) {
        // Typed text with no confirmed catalogue match (selectedServiceCategory
        // is cleared on every keystroke — see the input's onChange — and only
        // set by actually picking a suggestion). Previously this fell through
        // to `category: searchQuery`, seeding the booking wizard with
        // arbitrary unvalidated text that only failed much later, deep in the
        // flow, once no helpers matched a category that was never real.
        // Reject here instead: reopen real suggestions, or say so plainly.
        setServiceOpen(true);
        if (filteredServices.length === 0) {
          toast.error("We couldn't find that service. Try a different search, or browse all services.");
        }
        return;
      }
      params.set("task", searchQuery); params.set("category", selectedServiceCategory);
      // Not the typed text: it is free text and can name a person or a place
      // (M11). The category is one of ours; the length is enough to study
      // how people search.
      trackAnalyticsEvent('search_performed', { query_length: searchQuery.length, category: selectedServiceCategory, source: 'hero' });
    }
    navigate("/booking-flow?" + params.toString());
  };

  const handleCategoryClick = (query: string) => {
    trackAnalyticsEvent('search_performed', { query, category: query, source: 'category_tile' });
    navigate("/booking-flow?" + new URLSearchParams({ task: query, category: query }).toString());
  };

  return (
    <div className="min-h-dvh bg-background flex flex-col pb-[max(1rem,env(safe-area-inset-bottom,1rem))]">
      <SkipToContent />
      <Header />
      <main id="main-content" className="flex-1">

        {/* HERO SECTION */}
        <section className="pt-4 pb-0 overflow-hidden bg-brand-navy">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {/* No figure: the old "2,500+ families" was a placeholder (removed
                9 Oct). The owner wants the reassurance without a number. A new
                key, not heroTrustBadge: a content_translations override written
                for the old one could still carry the figure or {count} (Codex). */}
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 bg-white/10 border border-white/20">
                <ShieldCheck className="w-3.5 h-3.5 text-brand-amber" aria-hidden="true" />
                <span className="text-white/90 text-xs font-medium">{t('heroTrustedByFamilies')}</span>
              </div>
            </div>
            <div className="text-center mb-6">
              <h1 className="h-hero text-white leading-tight mb-3">
                Book your <span className="text-brand-amber">Kanfy</span> service
              </h1>
              <p className="text-sm sm:text-base max-w-md mx-auto leading-relaxed text-white/70">
                {t('heroSubtext')}
              </p>
            </div>
            {/* Task-first search — a single TaskRabbit-style pill with an
                integrated submit button and a typeahead dropdown of approved
                services. Location is collected inside the booking flow
                (dedicated location step), so the hero no longer asks for it. */}
            <div className="relative max-w-2xl mx-auto mb-6">
              <div ref={searchPillRef} className="flex items-center gap-1 bg-white rounded-full shadow-2xl border border-black/5 pl-5 pr-1.5 py-1.5">
                <Search className="w-5 h-5 text-muted-foreground shrink-0" />
                <input
                  type="text"
                  inputMode="search"
                  role="combobox"
                  aria-expanded={serviceOpen}
                  aria-label={t('whatServiceNeeded')}
                  placeholder={t('heroTaskPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSelectedServiceCategory(''); setServiceOpen(true); }}
                  onFocus={() => setServiceOpen(true)}
                  onBlur={() => setTimeout(() => setServiceOpen(false), 150)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="flex-1 min-w-0 px-2 py-2.5 bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  aria-label={t('findHelpersButton')}
                  className="shrink-0 flex items-center justify-center rounded-full bg-primary text-white h-11 w-14 hover:opacity-90 active:scale-95 transition"
                >
                  <Search className="w-5 h-5" />
                </button>
              </div>
              {serviceOpen && filteredServices.length > 0 && menuRect && createPortal(
                <div
                  role="listbox"
                  style={{
                    position: 'fixed',
                    top: menuRect.top,
                    left: menuRect.left,
                    width: menuRect.width,
                    maxHeight: menuRect.maxHeight,
                    zIndex: 60,
                  }}
                  className="overflow-y-auto overscroll-contain rounded-2xl border border-border bg-white shadow-2xl text-left"
                >
                  {filteredServices.map((opt, i) => (
                    <button
                      key={opt.label + '|' + opt.category}
                      type="button"
                      role="option"
                      aria-selected={false}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleServicePick(opt)}
                      className={`w-full text-left px-5 py-3.5 text-[15px] text-foreground hover:bg-muted flex items-center justify-between gap-2 ${i > 0 ? 'border-t border-border' : ''}`}
                    >
                      <span className="font-medium">{opt.label}</span>
                      {opt.label !== opt.category && (
                        <span className="text-xs text-muted-foreground shrink-0">{opt.category}</span>
                      )}
                    </button>
                  ))}
                </div>,
                document.body,
              )}
            </div>
            {/* Hero panels - right-to-left marquee */}
            <div className="relative -mb-px overflow-hidden rounded-t-2xl">
              <div ref={marqueeRef} className="hero-marquee-track flex gap-3 py-2" style={{ width: 'max-content' }}>
                {[...heroServices, ...heroServices].map((svc, i) => (
                  <button
                    key={svc.title + "-" + i}
                    type="button"
                    onClick={() => handleCategoryClick(svc.title)}
                    className="relative flex-shrink-0 w-[78vw] sm:w-[320px] md:w-[360px] h-48 sm:h-56 rounded-2xl overflow-hidden cursor-pointer text-left hover:scale-[1.02] transition-transform duration-300"
                  >
                    <img src={svc.image} alt={svc.title} width={800} height={600} loading={i < 2 ? "eager" : "lazy"} decoding="async" className="absolute inset-0 w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="relative z-10 h-full flex flex-col items-start justify-end p-5">
                      <span className="text-white text-xs font-bold px-2.5 py-1 rounded-full mb-2 inline-block" style={{backgroundColor:'#0F766E'}}>{svc.badge}</span>
                      <h3 className="h-section text-white leading-tight">{svc.title}</h3>
                      <p className="text-xs text-white/85">{svc.subtitle}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* The stats bar (families served, average rating, response time) and
            the hero's "2,500+ families" badge were placeholder figures, not
            measured ones; removed until real numbers exist (owner request). */}

        {/* CATEGORY PILLS */}
        <section className="py-4 md:py-5 bg-muted">
          <div className="max-w-6xl mx-auto">
            {/* Scrollable chip row with fade edge indicators */}
            <div className="relative">
              {/* No left fade: the row starts at scroll position 0, so the
                  only thing it covered was the first chip. */}
              {/* Right fade */}
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-muted to-transparent z-10" />
              <div className="overflow-x-auto scrollbar-hide px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="flex gap-2 pb-1 pr-4" style={{ width: 'max-content' }}>
                  {serviceCategories.map((cat) => (
                    <button
                      key={cat.label}
                      type="button"
                      onClick={() => handleCategoryClick(cat.query)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border border-border/60 bg-card text-foreground hover:border-primary/30 hover:bg-primary/5 transition-all active:scale-95 whitespace-nowrap flex-shrink-0 shadow-md"
                    >
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES GRID */}
        <section className="py-8 bg-card">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="h-section text-foreground">{t('popularServices')}</h2>
                <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">{t('topRatedHelpersReady')}</p>
              </div>
              <button
                type="button"
                // "See all" beside "Popular Services" means the catalogue,
                // not the booking wizard. Dropping someone into step 1 of a
                // booking when they asked to browse is a different action than
                // the label promises — and it matters more now that the grid
                // shows a preview rather than the whole list.
                onClick={() => navigate("/services/all")}
                // clay (--primary) as bare text on --card computes to ~1.16:1
                // in dark mode (clay and the dark teal card are nearly the
                // same luminance) — clay is deliberately identical across
                // modes as a *fill* colour, but was never contrast-checked
                // as *text* against --card specifically. Local override
                // rather than touching the shared token; this same gap
                // likely affects other text-primary-on-Card instances
                // app-wide, flagged separately as a follow-up.
                className="text-xs font-semibold flex items-center gap-1 hover:underline text-primary dark:text-primary-foreground"
              >
                {t('seeAll')} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            {/* Two per row on a phone, not three. At 360px three cards are
                104px wide — a 104x78 photo with a title that wraps to three
                lines under it. Two gives a 160px card you can actually read,
                and the `min-[420px]` step (the same one the stats bar above
                uses) brings the third back on a larger handset. */}
            <div className="grid grid-cols-2 min-[420px]:grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
              {/* The home page shows a sample, not the catalogue: this grid was
                  fed every active category, which at two-per-row would be eight
                  screens of thumbnails and left "See all" above it with nothing
                  to do. The rest live behind that button. */}
              {services.slice(0, HOME_SERVICE_PREVIEW).map((svc) => (
                <button
                  key={svc.title}
                  type="button"
                  onClick={() => handleCategoryClick(svc.title)}
                  className="group bg-card rounded-2xl overflow-hidden border border-border shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-left"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={svc.image} alt={svc.title} width={800} height={600} decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                  <div className="p-2 sm:p-3">
                    <h3 className="font-bold text-xs sm:text-sm leading-snug mb-0.5 text-foreground">{svc.title}</h3>
                    {/* WCAG AA on small text: teal #0D9488 (3.74:1), amber-500
                        (2.14:1) and gray-400 (2.53:1) all failed on white.
                        Darkened to #0F766E / amber-700 / gray-600 (all ≥4.5:1);
                        the star icon keeps its amber fill (graphical). */}
                    {/* Same white-card-only tuning as the rating below —
                        #0F766E is too close to the dark card's own teal to
                        read in dark mode; a lighter teal clears it there. */}
                    {/* Real data only — each row omitted when there is none, so
                        the card never shows an invented price/rating/count.
                        A category with no rated helpers simply shows its
                        availability; a brand-new one shows nothing extra. */}
                    {svc.price ? (
                      <p className="text-xs sm:text-sm font-semibold mb-1 truncate text-[#0F766E] dark:text-[#B8F9EE]">{svc.price}</p>
                    ) : (
                      /* A category nobody offers yet rendered a title over blank
                         space, which reads as broken rather than as "none yet"
                         (#688). Say so, and make it a recruiting surface. */
                      <p className="text-xs sm:text-sm mb-1 truncate text-muted-foreground">
                        {svc.helpers ? 'Pricing on request' : 'No helpers yet \u2014 be the first'}
                      </p>
                    )}
                    {(svc.rating || svc.helpers) && (
                      <div className="flex items-center justify-between">
                        {/* amber-700's dark-mode color comes from a global
                            `.dark .text-amber-700` override in index.css (a
                            badge-color block), not a local dark: variant here. */}
                        {svc.rating ? (
                          <span className="flex items-center gap-0.5 text-amber-700 text-xs sm:text-sm font-semibold">
                            <Star className="w-2.5 h-2.5 fill-yellow-400 text-brand-amber" />
                            {svc.rating.toFixed(1)}
                          </span>
                        ) : <span />}
                        {svc.helpers && (
                          <span className="text-muted-foreground text-xs sm:text-sm truncate">
                            {svc.helpers} {svc.helpers === 1 ? 'helper' : 'helpers'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <HowItWorksSimple />

        {/* TRUST & SAFETY */}
        <PremiumComparisonTable />

        <TrustSafety />

        {/* CTA BANNER */}
        <section className="py-10 sm:py-14" style={{ background: 'var(--th-grad-navy)' }}>
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-5 bg-brand-amber/20 border border-brand-amber/30">
              <ShieldCheck className="w-3.5 h-3.5 text-brand-amber" />
              <span className="text-xs font-semibold text-brand-amber">{t('verifiedBackgroundChecked')}</span>
            </div>
            <h2 className="h-hero text-white mb-3 leading-tight">
              {t('ctaHeadline1')}<br />{t('ctaHeadlineHighlight')}
            </h2>
            <p className="text-sm mb-6 leading-relaxed text-white/70">
              {t('ctaSubtextNoCount')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={() => navigate("/booking-flow")}
                className="flex items-center justify-center gap-2 font-black rounded-2xl px-8 py-4 text-sm active:scale-95 transition-all bg-brand-amber text-foreground"
              >
                <Search className="w-4 h-4" />
                {t('findHelperNow')}
              </button>
              <button
                type="button"
                onClick={() => navigate("/auth?mode=helper")}
                className="flex items-center justify-center gap-2 text-white font-semibold rounded-2xl px-8 py-4 text-sm active:scale-95 transition-all bg-white/10 border border-white/20"
              >
                <Shield className="w-4 h-4" />
                {t('becomeHelper')}
              </button>
            </div>
            <div className="mt-6 flex items-center justify-center gap-1 text-xs text-white/50">
              <Phone className="w-3.5 h-3.5" />
              <span>{t('whatsappSupport')} {config.support.whatsapp}</span>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ />

      </main>
      <Footer />
    </div>
  );
}
